// Final Hub Center — repository model + pure logic.
//
// Replaces the packet's illustrative scaffold (CommandCenterHub.data.ts). Every
// tile path here is a REAL, registered Square Code V2 route (see router.tsx),
// derived from the same typed data the rest of the command center uses
// (DIVISIONS + divisionWorkspaceTarget). No illustrative strings ship.
//
// The keyboard/command/tool helpers are pure so they can be unit-tested under
// the repo's node test env (no jsdom) and are the SINGLE source of truth the
// hub component imports — tests exercise the real decision logic, not a copy.

import { COMMAND_CENTER_ROOT } from '../commandCenterRoutes'
import type { CommandApiResult, DailyStatus } from "../../../api/client";
import { DIVISIONS, divisionWorkspaceTarget } from "../commandCenterData";
import type { CommandResult, HubRoute } from "./CommandCenterHub.types";

// The V2 command-center root. Every hub destination is prefixed with this and
// is a registered <Route> under the AccessGate-gated top-level tree.
export const HUB_ROOT = COMMAND_CENTER_ROOT;

// Per-tile neon accents — the approved Final Hub Center visual authority
// (SQ_DESIGN_LANGUAGE §3). Component-scoped identity colors, not global tokens.
const QUICK_ACCENT: Record<string, string> = {
  divisions: "#33e6cf",
  projects: "#a97bff",
  notifications: "#ffb62e",
  calendar: "#3ff09a",
  agents: "#38b6ff",
};

const DIVISION_ACCENT: Record<string, string> = {
  strategy: "#7ba8ff",
  operations: "#38b6ff",
  finance: "#2fe6a6",
  advisory: "#9f8cff",
  comms: "#c96bff",
  capital: "#ffb62e",
  creative: "#ff5fb3",
  olivium: "#ff5c5c",
  engineering: "#35e0d4",
  vault: "#57ef85",
};

/** Real route for a division, resolved from its home id (security->olivium, */
/** wellness->vault; operations has its own mini-portal home).               */
export function divisionRoute(homeId: string): string {
  const slug = divisionWorkspaceTarget(homeId);
  if (slug === "operations") return `${HUB_ROOT}/operations`;
  return `${HUB_ROOT}/workspaces/${slug}`;
}

/** Availability gate derived from the auth snapshot — mirrors AccessGate. */
export interface HubGate {
  status: "ready" | "loading" | "blocked";
  reason?: string;
}

export interface AuthSnapshot {
  loading: boolean;
  user: unknown;
  approved: boolean | null;
}

/**
 * Translate the auth snapshot into a tile gate. `null` (no provider / SSR)
 * resolves to `ready` so the presentational render shows navigable tiles; in
 * production the route lives under AccessGate, so this is authenticated +
 * approved by the time it renders.
 */
export function gateFromAuth(auth: AuthSnapshot | null): HubGate {
  if (!auth) return { status: "ready" };
  if (auth.loading) return { status: "loading", reason: "Checking access." };
  if (!auth.user) return { status: "blocked", reason: "Sign in to continue." };
  if (auth.approved === false) return { status: "blocked", reason: "Access not approved." };
  if (auth.approved === null) return { status: "loading", reason: "Verifying access." };
  return { status: "ready" };
}

function applyGate(route: Omit<HubRoute, "availability" | "reason">, gate: HubGate): HubRoute {
  if (gate.status === "ready") return { ...route, availability: "ready" };
  return { ...route, availability: gate.status === "loading" ? "loading" : "blocked", reason: gate.reason };
}

/** Quick Access tiles — Divisions / Projects / Notifications / Calendar / */
/** Agents (added 2026-07-14 — see docs/AGENT_RUNTIME_RECONCILIATION.md). */
export function buildQuickAccess(gate: HubGate = { status: "ready" }): HubRoute[] {
  const items: Omit<HubRoute, "availability" | "reason">[] = [
    { id: "divisions", label: "Divisions", description: "Open a division workspace", path: `${HUB_ROOT}/workspaces/all`, accent: QUICK_ACCENT.divisions, icon: "divisions" },
    { id: "projects", label: "Projects", description: "Open the project portfolio", path: `${HUB_ROOT}/projects`, accent: QUICK_ACCENT.projects, icon: "projects" },
    { id: "notifications", label: "Notifications", description: "Review what needs attention", path: `${HUB_ROOT}/notifications`, accent: QUICK_ACCENT.notifications, icon: "notifications" },
    { id: "calendar", label: "Calendar", description: "Open the Quam Calendar", path: `${HUB_ROOT}/calendar`, accent: QUICK_ACCENT.calendar, icon: "calendar" },
    // Reuses the existing "operations" icon glyph rather than adding a new
    // one to the closed HubIconName set / CommandCenterIcon component —
    // narrowest additive change, no icon-system fork.
    { id: "agents", label: "Agents", description: "Roster, approvals, and readiness across the governed agent system", path: `${HUB_ROOT}/agents`, accent: QUICK_ACCENT.agents, icon: "operations" },
  ];
  return items.map((item) => applyGate(item, gate));
}

/** Division tiles — the ten divisions, in the display order + names the rest */
/** of the command center uses (Security->Olivium, Wellness->Vault).          */
export function buildDivisions(gate: HubGate = { status: "ready" }): HubRoute[] {
  return DIVISIONS.map((division) => {
    const slug = divisionWorkspaceTarget(division.id);
    const route: Omit<HubRoute, "availability" | "reason"> = {
      id: slug,
      label: division.name,
      description: `Open ${division.name} workspace`,
      path: divisionRoute(division.id),
      accent: DIVISION_ACCENT[slug] ?? "#37e0ff",
      icon: slug as HubRoute["icon"],
    };
    return applyGate(route, gate);
  });
}

// ── Command dispatch mapping ────────────────────────────────────────────────
// The platform command system speaks CommandApiResult.status + .message; the
// hub speaks {ok,reason}. This is the mandated adapter (there is no {ok,reason}
// anywhere in the real command system) — never fabricate success.

const OK_STATUSES = new Set(["done", "dispatched", "running"]);

export function mapCommandResult(result: CommandApiResult | null): CommandResult {
  if (!result) return { ok: false, reason: "Command dispatch failed." };
  if (OK_STATUSES.has(result.status)) {
    return { ok: true, reason: result.message ?? undefined };
  }
  return { ok: false, reason: result.message ?? `Command ${result.status}.` };
}

// ── Daily / Video gating ────────────────────────────────────────────────────
// Fail closed on the server's real DailyStatus; surface its reason verbatim.

export function mapDailyStatusToResult(status: DailyStatus): CommandResult {
  if (status.configured && status.meeting_mode === "ready_for_internal_test") {
    return { ok: true };
  }
  if (status.meeting_mode === "blocked_missing_api_key") {
    return { ok: false, reason: "Video calling is not configured (DAILY_API_KEY not set)." };
  }
  if (status.meeting_mode === "blocked_migration_not_applied") {
    return { ok: false, reason: "Video calling is not ready (meetings schema not applied)." };
  }
  return { ok: false, reason: "Video calling is not available." };
}

// ── Upload validation (client-side; the server does not gate type/size) ──────

export const HUB_MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB
export const IMAGE_ACCEPT = ["image/"];
export const CODE_EXTENSIONS = [
  ".ts", ".tsx", ".js", ".jsx", ".json", ".css", ".html", ".md", ".py", ".rb",
  ".go", ".rs", ".java", ".c", ".h", ".cpp", ".sh", ".sql", ".yml", ".yaml", ".txt",
];

export function validateUploadFile(
  file: { name: string; size: number; type: string },
  opts: { maxBytes?: number; mimePrefixes?: string[]; extensions?: string[] } = {},
): CommandResult {
  const maxBytes = opts.maxBytes ?? HUB_MAX_UPLOAD_BYTES;
  if (file.size > maxBytes) {
    return { ok: false, reason: `File is larger than ${Math.round(maxBytes / (1024 * 1024))} MB.` };
  }
  if (opts.mimePrefixes && !opts.mimePrefixes.some((p) => file.type.startsWith(p))) {
    return { ok: false, reason: "That file type is not accepted here." };
  }
  if (opts.extensions) {
    const lower = file.name.toLowerCase();
    if (!opts.extensions.some((ext) => lower.endsWith(ext))) {
      return { ok: false, reason: "That file type is not accepted here." };
    }
  }
  return { ok: true };
}

// ── Keyboard decision helpers (pure; imported by the hub component) ──────────

export interface KeyState {
  key: string;
  shiftKey: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}

/** Enter submits (not Shift+Enter, not during IME composition or open canvas). */
export function shouldSubmit(e: KeyState, composing: boolean, canvasOpen: boolean): boolean {
  return e.key === "Enter" && !e.shiftKey && !composing && !canvasOpen;
}

/** Shift+Enter inserts a newline (the textarea default; never submits). */
export function shouldInsertNewline(e: KeyState): boolean {
  return e.key === "Enter" && e.shiftKey;
}

/** A bare `p`/`P` keypress that is eligible to arm the double-P tool shortcut. */
export function isDoublePCandidate(e: KeyState, commandEmpty: boolean, canvasOpen: boolean, composing: boolean): boolean {
  if (e.key !== "p" && e.key !== "P") return false;
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return false;
  return commandEmpty && !canvasOpen && !composing;
}

/**
 * Whether the command field is eligible for the double-P shortcut. The first
 * `p` is NOT prevented, so by the time the second `p` keydown fires it has
 * already been inserted into the textarea — the field is no longer empty. Once
 * armed (the first `p` fired within the window), a field that holds only that
 * `p`/`P` is still eligible, so the second tap opens the tool canvas (and the
 * inserted `p` is cleared). Without armed, only a truly empty field is eligible.
 */
export function doublePFieldEligible(trimmedCommand: string, armed: boolean): boolean {
  if (trimmedCommand === "") return true;
  return armed && (trimmedCommand === "p" || trimmedCommand === "P");
}
